import { useState } from 'react';
import { getCbsOverzicht, useRequest } from '../../requests';
import { CbsOverzicht as CbsOverzichtRow, DbUser } from '../../types';
import ExcelCbsOverzicht from '../../comp/report/ExcelCbsOverzicht';
import formatDate from '../../util/formatDate';
import halfYearPeriods from '../../util/halfYearPeriods';

interface CbsOverzichtProps {
	dbUser: DbUser,
}

const COLUMNS: { label: string, value: (row: CbsOverzichtRow) => string }[] = [
	{label: 'BSN', value: row => row.bsn ?? ''},
	{label: 'Geslacht', value: row => row.geslacht},
	{label: 'Geboortedatum', value: row => row.geboortedatum ? formatDate(row.geboortedatum, 2) : ''},
	{label: 'Gemeente', value: row => row.gemeente_naam ?? ''},
	{label: 'Startdatum', value: row => formatDate(row.startdatum, 2)},
	{label: 'Soort verwijzer', value: row => row.soort_verwijzer ?? ''},
	{label: 'Datum stopbericht', value: row => row.stopdatum ? formatDate(row.stopdatum, 2) : ''},
	{label: 'Reden beëindiging', value: row => row.reden_beeindiging ?? ''},
];

const PRESETS = halfYearPeriods(new Date(), 4);

const CbsOverzicht: React.FC<CbsOverzichtProps> = ({dbUser}): JSX.Element => {
	const [van, setVan] = useState(PRESETS[0].van);
	const [tot, setTot] = useState(PRESETS[0].tot);
	const validPeriod = van !== '' && tot !== '' && van <= tot;

	const {data, isLoading} = useRequest(getCbsOverzicht, {
		token: dbUser.token,
		provider: dbUser.aanbieder,
		van,
		tot,
	}, {enabled: validPeriod});
	const rows = validPeriod ? data?.response ?? [] : [];

	return (
		<section className='client-data'>
			<section className='table-element'>
				<section className='table-header'>
					<h2>CBS overzicht jeugd</h2>
					<div className='filters'>
						<label>Verslagperiode</label>
						<label htmlFor='cbs-preset'>
							<select
								id='cbs-preset'
								value={PRESETS.find(p => p.van === van && p.tot === tot)?.van ?? ''}
								onChange={event => {
									const preset = PRESETS.find(p => p.van === event.target.value);
									if (preset) {
										setVan(preset.van);
										setTot(preset.tot);
									}
								}}>
								<option value=''>Eigen periode</option>
								{PRESETS.map(p => <option key={p.van} value={p.van}>{p.label}</option>)}
							</select>
						</label>
						<label htmlFor='cbs-van'>
							<span><strong>Van</strong></span>
							<input id='cbs-van' type='date' value={van} max={tot || undefined} onChange={event => setVan(event.target.value)} />
						</label>
						<label htmlFor='cbs-tot'>
							<span><strong>Tot en met</strong></span>
							<input id='cbs-tot' type='date' value={tot} min={van || undefined} onChange={event => setTot(event.target.value)} />
						</label>
						<label className='options'>Download</label>
						<label>
							{rows.length === 0 ? (
								<button className='button download disabled' disabled={true}>
									<strong>Excel</strong>
								</button>
							) : (
								<ExcelCbsOverzicht dbUser={dbUser} data={rows} columns={COLUMNS} periode={`${van} - ${tot}`} />
							)}
						</label>
					</div>
				</section>
				{!validPeriod ? (
					<section className='table-empty'>Kies een geldige verslagperiode</section>
				) : isLoading ? (
					<section className='table-empty'>Laden...</section>
				) : rows.length === 0 ? (
					<section className='table-empty'>Er zijn geen jeugdhulp toewijzingen die in deze periode actief waren</section>
				) : (
					<section className='table-scroll-x'>
						<table className='data-table'>
							<tbody>
								<tr>
									<td className='align-right'>#</td>
									{COLUMNS.map(column => <td key={column.label}>{column.label}</td>)}
								</tr>
								{rows.map((row, index) => (
									<tr key={row.toewijzing_id}>
										<td className='align-right'>{index + 1}</td>
										{COLUMNS.map(column => <td key={column.label}>{column.value(row)}</td>)}
									</tr>
								))}
							</tbody>
						</table>
					</section>
				)}
			</section>
		</section>
	);
};

export default CbsOverzicht;
